"use client"
import { Download, Calendar, User, BookOpen, Trash2, Play, Award, ExternalLink, Search, Filter, SortAsc, } from "lucide-react"
import "./AssignmentsPage.css"
import BackButton from "../shared/backButton"
import Modal from "../layout/Modal"
import { useState } from "react"
import { useNavigate } from "react-router-dom"

const AssignmentsPage = () => {
  const [assignments, setAssignments] = useState([
    {
      id: 1,
      candidateName: "Sarah Johnson",
      candidateRole: "Marketing Specialist",
      department: "Marketing",
      assignedCourses: [
        { platform: "coursera", title: "Data Science Fundamentals", status: "In Progress" },
        { platform: "udemy", title: "SEO Masterclass", status: "Completed" },
        { platform: "linkedin", title: "Strategic Thinking", status: "Not Started" },
      ],
      assignedDate: new Date("2025-06-20"),
      urgency: "High",
    },
    {
      id: 2,
      candidateName: "Mike Chen",
      candidateRole: "Software Developer",
      department: "Engineering",
      assignedCourses: [
        { platform: "udemy", title: "React Complete Guide", status: "In Progress" },
        { platform: "coursera", title: "Cloud Architecture on AWS", status: "Not Started" },
      ],
      assignedDate: new Date("2025-06-22"),
      urgency: "Medium",
    },
    {
      id: 3,
      candidateName: "Emily Rodriguez",
      candidateRole: "HR Coordinator",
      department: "Human Resources",
      assignedCourses: [
        { platform: "linkedin", title: "Conflict Resolution", status: "Completed" },
        { platform: "coursera", title: "Leadership and Management", status: "In Progress" },
        { platform: "custom", title: "HRIS Advanced Training", status: "Not Started" },
      ],
      assignedDate: new Date("2025-06-18"),
      urgency: "Low",
    },
    {
      id: 4,
      candidateName: "David Kim",
      candidateRole: "Sales Associate",
      department: "Sales",
      assignedCourses: [
        { platform: "udemy", title: "Sales Psychology & Persuasion", status: "In Progress" },
        { platform: "linkedin", title: "Customer Relations Excellence", status: "Completed" },
        { platform: "coursera", title: "Advanced Analytics", status: "Not Started" },
      ],
      assignedDate: new Date("2025-06-23"),
      urgency: "High",
    },
    {
      id: 5,
      candidateName: "Lisa Chang",
      candidateRole: "Product Manager",
      department: "Product",
      assignedCourses: [
        { platform: "coursera", title: "User Research Methods", status: "Completed" },
        { platform: "udemy", title: "A/B Testing Mastery", status: "In Progress" },
      ],
      assignedDate: new Date("2025-06-21"),
      urgency: "Medium",
    },
  ])

  const navigate = useNavigate()
  const [showModal, setShowModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
    itemId: null,
  })

    // Search and Filter States
    const [searchTerm, setSearchTerm] = useState("")
    const [filterDepartment, setFilterDepartment] = useState("All")
    const [filterUrgency, setFilterUrgency] = useState("All")
    const [filterStatus, setFilterStatus] = useState("All")
    const [sortBy, setSortBy] = useState("assignedDate")
  
    // Filter Options
    const departments = ["All", "Marketing", "Engineering", "Human Resources", "Sales", "Product", "Design", "Analytics"]
    const urgencyLevels = ["All", "High", "Medium", "Low"]
    const statusOptions = ["All", "Completed", "In Progress", "Not Started"]
    const sortOptions = [
      { value: "assignedDate", label: "Assignment Date" },
      { value: "candidateName", label: "Candidate Name" },
      { value: "department", label: "Department" },
      { value: "urgency", label: "Urgency" },
      { value: "progress", label: "Progress" },
    ]

  const getPlatformIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case "coursera":
        return <BookOpen size={16} />
      case "udemy":
        return <Play size={16} />
      case "linkedin":
        return <Award size={16} />
      default:
        return <ExternalLink size={16} />
    }
  }

  const getPlatformColor = (platform) => {
    switch (platform.toLowerCase()) {
      case "coursera":
        return "platform-coursera-unique"
      case "udemy":
        return "platform-udemy-unique"
      case "linkedin":
        return "platform-linkedin-unique"
      default:
        return "platform-custom-unique"
    }
  }

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "course-status-completed-unique"
      case "in progress":
        return "course-status-progress-unique"
      case "not started":
        return "course-status-pending-unique"
      default:
        return "course-status-pending-unique"
    }
  }

   // Filter and Sort Logic
   const getProgressPercentage = (assignment) => {
    const completed = assignment.assignedCourses.filter((c) => c.status.toLowerCase() === "completed").length
    return (completed / assignment.assignedCourses.length) * 100
  }

  const sortData = (data) => {
    return [...data].sort((a, b) => {
      switch (sortBy) {
        case "assignedDate":
          return new Date(b.assignedDate) - new Date(a.assignedDate)
        case "candidateName":
          return a.candidateName.localeCompare(b.candidateName)
        case "department":
          return a.department.localeCompare(b.department)
        case "urgency":
          const urgencyOrder = { High: 3, Medium: 2, Low: 1 }
          return urgencyOrder[b.urgency] - urgencyOrder[a.urgency]
        case "progress":
          return getProgressPercentage(b) - getProgressPercentage(a)
        default:
          return 0
      }
    })
  }

  const filteredData = assignments.filter((assignment) => {
    const matchesSearch =
      assignment.candidateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.candidateRole.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
      assignment.assignedCourses.some((course) => course.title.toLowerCase().includes(searchTerm.toLowerCase()))

    const matchesDepartment = filterDepartment === "All" || assignment.department === filterDepartment
    const matchesUrgency = filterUrgency === "All" || assignment.urgency === filterUrgency

    const matchesStatus =
      filterStatus === "All" || assignment.assignedCourses.some((course) => course.status === filterStatus)

    return matchesSearch && matchesDepartment && matchesUrgency && matchesStatus
  })

  const sortedData = sortData(filteredData)

  const downloadExcel = () => {
    // Create CSV content
    const headers = [
      "Candidate Name",
      "Role",
      "Department",
      "Course Title",
      "Platform",
      "Status",
      "Assigned Date",
      "Urgency",
    ]

    const csvRows = []
    assignments.forEach((assignment) => {
      assignment.assignedCourses.forEach((course) => {
        csvRows.push(
          [
            assignment.candidateName,
            assignment.candidateRole,
            assignment.department,
            `"${course.title}"`,
            course.platform,
            course.status,
            assignment.assignedDate.toLocaleDateString(),
            assignment.urgency,
          ].join(","),
        )
      })
    })

    const csvContent = [headers.join(","), ...csvRows].join("\n")

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `training-assignments-${new Date().toISOString().split("T")[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const handleRemove = (assignment) => {
    setShowModal({
      show: true,
      type: "warning",
      title: "Remove Training Assignment",
      message: `Are you sure you want to remove all training assignments for ${assignment.candidateName}? This action cannot be undone.`,
      itemId: assignment.id,
    })
  }

  const confirmRemove = () => {
    if (showModal.itemId) {
      setAssignments((prev) => prev.filter((item) => item.id !== showModal.itemId))
      setShowModal({
        show: false,
        type: "",
        title: "",
        message: "",
        itemId: null,
      })
    }
  }

  const getTotalCourses = () => {
    return assignments.reduce((total, assignment) => total + assignment.assignedCourses.length, 0)
  }

  const getCompletedCourses = () => {
    return assignments.reduce((total, assignment) => {
      return total + assignment.assignedCourses.filter((course) => course.status.toLowerCase() === "completed").length
    }, 0)
  }

  return (
    <div className="assignments-page">
      <BackButton />

      <main className="assignments-main">
        <div className="container">
          <div className="page-header">
            <div className="header-icon">
              <BookOpen size={32} />
            </div>
            <h1>Training Assignments</h1>
            <p>Track and manage all assigned training programs and course progress</p>
          </div>

          {/* Search and Filter Section */}
          <div className="assignments-search-filters-unique">
            <div className="assignments-filters-row-unique">
              <div className="assignments-search-container-unique">
                <Search size={20} className="assignments-search-icon-unique" />
                <input
                  type="text"
                  placeholder="Search by name, role, department, or course..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="assignments-search-input-unique"
                />
              </div>

              <div className="assignments-filter-group-unique">
                <Filter size={16} />
                <label>Department:</label>
                <select
                  value={filterDepartment}
                  onChange={(e) => setFilterDepartment(e.target.value)}
                  className="assignments-filter-select-unique"
                >
                  {departments.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
              </div>

              <div className="assignments-filter-group-unique">
                <Filter size={16} />
                <label>Urgency:</label>
                <select
                  value={filterUrgency}
                  onChange={(e) => setFilterUrgency(e.target.value)}
                  className="assignments-filter-select-unique"
                >
                  {urgencyLevels.map((urgency) => (
                    <option key={urgency} value={urgency}>
                      {urgency}
                    </option>
                  ))}
                </select>
              </div>

              <div className="assignments-filter-group-unique">
                <Filter size={16} />
                <label>Status:</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="assignments-filter-select-unique"
                >
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              <div className="assignments-filter-group-unique">
                <SortAsc size={16} />
                <label>Sort by:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="assignments-filter-select-unique"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="aactions-bar">
            <div className="astats">
              <span className="astat-item">
                Total Candidates: <strong>{assignments.length}</strong>
              </span>
              <span className="astat-item">
                Total Courses: <strong>{getTotalCourses()}</strong>
              </span>
              <span className="astat-item">
                Completed: <strong>{getCompletedCourses()}</strong>
              </span>
            </div>
            {assignments.length > 0 && (
              <button className="btn btn-primary" onClick={downloadExcel}>
                <Download size={16} />
                Download Data
              </button>
            )}
          </div>

          {assignments.length === 0 ? (
            <div className="empty-state">
              <BookOpen size={48} />
              <h3>No Training Assignments Yet</h3>
              <p>Start by visiting the Upskill Insights page to assign training to team members.</p>
              <button className="btn btn-primary" onClick={() => navigate("/talent-development/upskill")}>
                Go to Upskill Insights
              </button>
            </div>
          ) : (
            <div className="assignments-grid-unique">
              {assignments.map((assignment) => (
                <div key={assignment.id} className="assignment-card-unique">
                  <div className="assignment-card-header-unique">
                    <div className="candidate-info-unique">
                      <div className="candidate-avatar-unique">
                        <User size={24} />
                      </div>
                      <div className="candidate-details-unique">
                        <h3 className="candidate-name-unique">{assignment.candidateName}</h3>
                        <p className="candidate-role-unique">{assignment.candidateRole}</p>
                        <p className="candidate-department-unique">{assignment.department}</p>
                      </div>
                    </div>
                    <div className="assignment-actions-unique">
                      <span className={`urgency-badge-unique urgency-${assignment.urgency.toLowerCase()}-unique`}>
                        {assignment.urgency} Priority
                      </span>
                      <button
                        className="remove-assignment-btn-unique"
                        onClick={() => handleRemove(assignment)}
                        title="Remove all assignments"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="assignment-meta-unique">
                    <div className="meta-item-unique">
                      <Calendar size={16} />
                      <span>Assigned: {assignment.assignedDate.toLocaleDateString()}</span>
                    </div>
                    <div className="meta-item-unique">
                      <BookOpen size={16} />
                      <span>
                        {assignment.assignedCourses.length} Course{assignment.assignedCourses.length !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>

                  <div className="courses-section-unique">
                    <h4 className="courses-title-unique">Assigned Courses:</h4>
                    <div className="courses-list-unique">
                      {assignment.assignedCourses.map((course, index) => (
                        <div key={index} className="course-item-unique">
                          <div className="course-info-unique">
                            <div className={`course-platform-unique ${getPlatformColor(course.platform)}`}>
                              {getPlatformIcon(course.platform)}
                              <span className="platform-name-unique">{course.platform}</span>
                            </div>
                            <div className="course-title-unique">{course.title}</div>
                          </div>
                          <div className={`course-status-unique ${getStatusColor(course.status)}`}>{course.status}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="assignment-progress-unique">
                    <div className="progress-stats-unique">
                      <span className="progress-label-unique">Progress:</span>
                      <span className="progress-value-unique">
                        {assignment.assignedCourses.filter((c) => c.status.toLowerCase() === "completed").length} /{" "}
                        {assignment.assignedCourses.length} completed
                      </span>
                    </div>
                    <div className="progress-bar-unique">
                      <div
                        className="progress-fill-unique"
                        style={{
                          width: `${(assignment.assignedCourses.filter((c) => c.status.toLowerCase() === "completed").length / assignment.assignedCourses.length) * 100}%`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Modal
        show={showModal.show}
        onClose={() =>
          setShowModal({
            show: false,
            type: "",
            title: "",
            message: "",
            itemId: null,
          })
        }
        type={showModal.type}
        title={showModal.title}
        message={showModal.message}
      >
        {showModal.type === "warning" && (
          <div
            style={{
              display: "flex",
              gap: "12px",
              justifyContent: "center",
              marginTop: "16px",
            }}
          >
            <button
              className="btn btn-outline"
              onClick={() =>
                setShowModal({
                  show: false,
                  type: "",
                  title: "",
                  message: "",
                  itemId: null,
                })
              }
            >
              Cancel
            </button>
            <button className="btn btn-primary" onClick={confirmRemove}>
              Remove
            </button>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default AssignmentsPage
